import React, { useState } from 'react';
import './SearchBox.css';
import SearchResultsModal from '../SearchResultsModal/SearchResultsModal';
import type { Table } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '../../i18n/I18nContext';
import { includesNormalizedSearchText, normalizeSearchText } from '../../utils/textSearch';

// Props for the SearchBox component.
export interface SearchBoxProps {
  tables: Table[];
  onSelectTable: (table: Table) => void;
  showToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const SearchBox: React.FC<SearchBoxProps> = ({ tables = [], onSelectTable, showToast }) => {
  const { t } = useI18n();
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<Table[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);

  // Perform the search based on the current query and tables
  function performSearch() {
    const term = normalizeSearchText(query);
    if (term.length < 3) return;
    const matches = tables.filter(t => {
      const nameMatch = includesNormalizedSearchText(t.name, term);
      const personMatch = includesNormalizedSearchText(t.person, term);
      const productsMatch = t.products && t.products.some(p => includesNormalizedSearchText(p.name, term));
      return Boolean(nameMatch || personMatch || productsMatch);
    });
    if (matches.length === 0) {
      showToast(t('search.noMatches'), 'error');
    } else if (matches.length === 1) {
      onSelectTable(matches[0]);
    } else {
      setResults(matches);
      setShowResults(true);
    }
  }

  // Handle keydown in the input; pressing Enter triggers search
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      const term = query.trim();
      if (term.length >= 3) {
        e.preventDefault();
        performSearch();
      }
    }
  }

  return (
    <div className="search-container">
      <div className="search-input-shell">
        <span className="search-input-icon">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </span>
        <input
          type="text"
          placeholder={t('search.placeholder')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {query && (
          <button
            type="button"
            className="search-clear-button"
            aria-label={t('search.clear')}
            onClick={() => setQuery('')}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>
      <button className="search-button" onClick={performSearch} disabled={query.trim().length < 3}>
        {t('search.button')}
      </button>
      {showResults && (
        <SearchResultsModal
          searchResults={results}
          onSelectItem={item => {
            setShowResults(false);
            onSelectTable(item);
          }}
          onClose={() => setShowResults(false)}
        />
      )}
    </div>
  );
};

export default SearchBox;
